/**
 * The client-side error vocabulary — T007.
 *
 * Split out of `src/lib/api.ts` deliberately. Anything that renders a failure needs
 * `TransportError`, and importing it from the transport module would pull `firebase/functions` into
 * the entry bundle for every visitor, including a signed-out one who never makes a call. This module
 * imports no Firebase package at all.
 */
import {
  ERROR_MESSAGE_KEYS,
  fieldIssuesFromZodError,
  freezeFieldIssues,
  isErrorCode,
  isMessageKey,
  NO_FIELD_ISSUES,
  parseFieldIssues,
  type ErrorCode,
  type FieldIssue,
  type MessageKey,
} from '@simple-qa/shared';

/**
 * The field-issue vocabulary is **not** redeclared here.
 *
 * `contracts/operations.md` § Common envelope and `BOUNDARIES.md` § 6 make field-level detail part
 * of the `INVALID_ARGUMENT` contract, and the accepted architecture has the client compile against
 * `packages/shared`. So `FieldIssue`, its keys, its parser and its Zod mapper all live there and are
 * imported by `functions/src/lib/dispatch.ts` and by this module alike — one declaration, one set of
 * tests, no possibility of the two sides drifting while both suites stay green.
 */
export {
  createFieldIssue,
  FIELD_ISSUE_KEYS,
  fieldIssueSchema,
  NO_FIELD_ISSUES,
  parseFieldIssues,
  ROOT_ISSUE_PATH,
  type FieldIssue,
} from '@simple-qa/shared';

/**
 * Field issues read off an error the **client** threw while validating a request or a response.
 *
 * A thin, named wrapper over the shared mapper: the prefix is what makes a locally rejected request
 * describe itself the way the server would. The dispatcher validates the whole envelope and reports
 * `params.marker`; the client validates the params alone, so it passes `"params"` and reports the
 * same path.
 */
export function fieldIssuesFromThrown(error: unknown, prefix: string): readonly FieldIssue[] {
  return fieldIssuesFromZodError(error, prefix);
}

function readProperty(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined;
}

/**
 * Every failure the client sees, as one of the eight stable codes plus the Georgian message *key*
 * for it, plus the field-level detail an `INVALID_ARGUMENT` carries. The client never renders a
 * server-composed string (`BOUNDARIES.md` § 6) — the key is resolved by `src/app/messages.ts`.
 */
export class TransportError extends Error {
  readonly code: ErrorCode;
  readonly messageKey: MessageKey;
  /** Frozen and always present; empty for every code that carries no field detail. */
  readonly issues: readonly FieldIssue[];

  /**
   * The original failure travels in the standard `cause`, so a developer sees it in the console
   * without it ever being a field the interface could render by accident.
   */
  constructor(code: ErrorCode, detail: string, cause?: unknown, issues: readonly FieldIssue[] = NO_FIELD_ISSUES) {
    super(detail, cause === undefined ? undefined : { cause });
    this.name = 'TransportError';
    this.code = code;
    // R08: a refusal by the server's write fence is worded as the maintenance it is.
    this.messageKey = issues.some((issue) => issue.code === 'write_fence') ? 'error.writeFence' : ERROR_MESSAGE_KEYS[code];
    // Deep copy, not a stored reference: `readonly FieldIssue[]` is a compile-time promise only, so
    // a caller that keeps its own mutable array could otherwise change what this error carries —
    // or mutate an entry in place — long after it was thrown.
    this.issues = freezeFieldIssues(issues);
  }
}

/**
 * The callable protocol's own codes → the eight stable codes, the inverse of the server's
 * `CALLABLE_ERROR_CODES`. It is a fallback: the server sends its stable code in `details`, and this
 * table is what keeps a failure produced *by the protocol itself* — an unreachable backend, a
 * cancelled request, a response the SDK could not decode — from arriving as something meaningless.
 */
const PROTOCOL_CODES: Readonly<Record<string, ErrorCode>> = Object.freeze({
  'functions/unauthenticated': 'UNAUTHENTICATED',
  'functions/permission-denied': 'FORBIDDEN',
  'functions/not-found': 'NOT_FOUND',
  'functions/invalid-argument': 'INVALID_ARGUMENT',
  'functions/failed-precondition': 'FAILED_PRECONDITION',
  'functions/aborted': 'CONFLICT',
  'functions/already-exists': 'CONFLICT',
  'functions/resource-exhausted': 'RATE_LIMITED',
});

/**
 * Turn whatever the SDK threw into a `TransportError`.
 *
 * Order matters. The server's own `details` payload is authoritative when it carries a stable code
 * *and* a well-formed message key — that is the contract. Anything else falls back to the protocol
 * code, and anything unrecognisable becomes `INTERNAL`, which is the only honest answer for a
 * failure the client cannot classify.
 */
export function decodeCallableError(error: unknown): TransportError {
  const details = readProperty(error, 'details');
  const detailsCode = readProperty(details, 'code');
  if (isErrorCode(detailsCode)) {
    const key = readProperty(details, 'messageKey');
    // The key is re-derived from the code rather than trusted: `TransportError` maps it itself, and
    // a mismatch is a server bug that must not become a wrong sentence on screen. The field issues,
    // in contrast, are data the server alone can produce, so they are carried through — validated,
    // never trusted blindly.
    const detail = isMessageKey(key) ? `server reported ${detailsCode} (${key})` : `server reported ${detailsCode}`;
    return new TransportError(detailsCode, detail, error, parseFieldIssues(readProperty(details, 'issues')));
  }

  const protocolCode = readProperty(error, 'code');
  if (typeof protocolCode === 'string') {
    const mapped = PROTOCOL_CODES[protocolCode];
    if (mapped !== undefined) {
      return new TransportError(mapped, `callable protocol error ${protocolCode}`, error);
    }
    return new TransportError('INTERNAL', `callable protocol error ${protocolCode}`, error);
  }

  return new TransportError('INTERNAL', 'the call failed for an unrecognised reason', error);
}
